import {
  Button,
  Chip,
  Divider,
  EButtonColorVariant,
  EChipSizeVariant,
  LinkButton,
  TextInput,
} from '@outdoorsyco/bonfire';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMenuTriggerState } from 'react-stately';

import { useBreakpointContext } from '@/components/common/BreakpointContext';
import { DatePickerModalContent } from '@/components/common/DatePickerModal/DatePickerModalContent';
import DateContent from '@/components/ui/SearchHeader/NavbarSearch/DesktopNavbarSearch/DateContent/DateContent';

import { FieldOverlay } from './FieldOverlay';
import { TDatePickerComponentFieldValue } from './types';

interface IDatesFieldProps {
  value?: TDatePickerComponentFieldValue;
  onChange: (value?: TDatePickerComponentFieldValue) => void;
}

// A single "Dates" trigger that opens a shared calendar overlay (the navbar's
// DateContent on tablet+ / a stacked, scrollable sheet below it). Shows the
// selected range until a date is picked, then the "Add dates" hint.
export const DatesField = ({ value, onChange }: IDatesFieldProps) => {
  const intl = useIntl();
  const { isAboveTablet } = useBreakpointContext();

  const triggerRef = useRef<HTMLDivElement>(null);
  const triggerState = useMenuTriggerState({});

  // The mobile sheet commits on Done (so the user can add flexibility after picking
  // the range), so its selection is held locally and re-seeded from the committed
  // value each time the sheet opens.
  const [pending, setPending] = useState<TDatePickerComponentFieldValue | undefined>(value);
  useEffect(() => {
    if (triggerState.isOpen) setPending(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerState.isOpen]);

  const handleTriggerClick = (): void => triggerState.open();

  // The trigger is read-only, so typing does nothing — open the calendar on
  // Enter/Space (matching native button activation) for keyboard users.
  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleTriggerClick();
    }
  };

  const disableBefore = useMemo(
    () => new Date(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate()),
    [],
  );

  const label = intl.formatMessage({
    id: 'EwSv0i',
    defaultMessage: 'Dates',
    description: 'Locality guided search > Dates field label',
  });
  const addDatesLabel = intl.formatMessage({
    id: 'HRyAIh',
    defaultMessage: 'Add Dates',
    description: 'Locality guided search > Initial step > Empty date field value',
  });

  const displayValue = (() => {
    if (value?.from && value?.to) {
      return `${dayjs(value.from).format('MMM D')} – ${dayjs(value.to).format('MMM D')}`;
    }
    if (value?.from) return dayjs(value.from).format('MMM D');
    return addDatesLabel;
  })();

  // Tablet+: commit on each change and close once a full range is picked, matching
  // the navbar date picker.
  const handleDatesChange = (dates?: TDatePickerComponentFieldValue): void => {
    const isEmpty = !dates?.from && !dates?.to && !dates?.flexible_days;
    onChange(isEmpty ? undefined : dates);
    if (dates?.from && dates?.to) triggerState.close();
  };

  const handleDone = (): void => {
    onChange(pending?.from && pending?.to ? pending : undefined);
    triggerState.close();
  };

  const setFlexibleDays = (flexible_days?: number) =>
    setPending(prev => ({ ...prev, flexible_days }));

  const content = isAboveTablet ? (
    <DateContent
      open={triggerState.isOpen}
      dates={value}
      disabledDays={{ before: disableBefore }}
      onChangeDates={handleDatesChange}
    />
  ) : (
    // isStacked → VirtualizedDatePicker (24 months); a single scrollable column that
    // fits the narrow sheet.
    <DatePickerModalContent
      isStacked
      dateFrom={pending?.from}
      dateTo={pending?.to}
      disableBefore={disableBefore}
      disabledDays={{ before: disableBefore }}
      onChange={({ from, to }) => setPending(prev => ({ ...prev, from, to }))}
    />
  );

  const footer = isAboveTablet ? undefined : (
    <div className="w-full">
      <Divider />
      <div className="flex items-center p-4 overflow-x-auto gap-2 scrollbar-none">
        <Chip
          content={intl.formatMessage({ defaultMessage: 'Match exact dates', id: 'uhcpp1' })}
          sizeVariant={EChipSizeVariant.Small}
          onClick={() => setFlexibleDays(undefined)}
          active={!pending?.flexible_days}
          withBoxShadow={false}
          contentClassName="whitespace-nowrap [&>p]:font-medium"
        />
        <Chip
          content={intl.formatMessage({ defaultMessage: '± 1 day', id: '9IdT1V' })}
          sizeVariant={EChipSizeVariant.Small}
          onClick={() => setFlexibleDays(1)}
          active={pending?.flexible_days === 1}
          withBoxShadow={false}
          contentClassName="whitespace-nowrap [&>p]:font-medium"
        />
        <Chip
          content={intl.formatMessage({ defaultMessage: '± 3 days', id: 'r4uykC' })}
          sizeVariant={EChipSizeVariant.Small}
          onClick={() => setFlexibleDays(3)}
          active={pending?.flexible_days === 3}
          withBoxShadow={false}
          contentClassName="whitespace-nowrap [&>p]:font-medium"
        />
      </div>
      <Divider />
      <div className="flex items-center justify-between p-4">
        <LinkButton
          label={intl.formatMessage({ defaultMessage: 'Clear', id: '/GCoTA' })}
          onClick={() => setPending(undefined)}
          disabled={!pending?.from && !pending?.to}
        />
        <Button
          variant={EButtonColorVariant.Primary}
          label={intl.formatMessage({ defaultMessage: 'Done', id: 'JXdbo8' })}
          onClick={handleDone}
          // Can't apply a half-picked range; an empty selection clears the dates.
          disabled={!!pending?.from && !pending?.to}
        />
      </div>
    </div>
  );

  return (
    <div ref={triggerRef}>
      <TextInput
        controlled
        readOnly
        label={label}
        ariaLabel={label}
        inputValue={displayValue}
        onChange={() => undefined}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        // Announce that this read-only field opens a dialog, and its open state.
        otherAriaProps={{ 'aria-haspopup': 'dialog', 'aria-expanded': triggerState.isOpen }}
      />

      <FieldOverlay
        triggerRef={triggerRef}
        triggerState={triggerState}
        content={content}
        footer={footer}
      />
    </div>
  );
};
